import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMiniESGStore } from '../store/miniESGStore';

export function MiniESGScoreCard() {
  const { t } = useTranslation();
  const store = useMiniESGStore();
  const { score } = store;

  const getScoreClass = (level: string) => {
    if (level === 'red') return 'score-red';
    if (level === 'yellow') return 'score-yellow';
    return 'score-green';
  };

  const getScoreColor = (level: string) => {
    if (level === 'red') return 'text-red-400 border-red-500/40';
    if (level === 'yellow') return 'text-yellow-400 border-yellow-500/40';
    return 'text-green-400 border-green-500/40';
  };

  return (
    <div className="space-y-6">
      {/* Total Score */}
      <div className={`border rounded-lg p-6 bg-[#0b0b0b] ${getScoreColor(score.level)}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-orbitron">{t("miniESG.totalScore") || "EVAT Mini ESG™ Total Score"}</h3>
          <div className={`text-4xl font-orbitron ${getScoreColor(score.level).split(' ')[0]}`}>
            {score.total} / 30
          </div>
        </div>
        <div className={`inline-block px-4 py-2 rounded text-sm font-orbitron ${
          score.level === 'red' ? 'bg-red-900/30 text-red-400' :
          score.level === 'yellow' ? 'bg-yellow-900/30 text-yellow-400' :
          'bg-green-900/30 text-green-400'
        }`}>
          {score.level === 'red' ? `🔴 ${t("miniESG.level.red") || "Basic Level"}` :
           score.level === 'yellow' ? `🟡 ${t("miniESG.level.yellow") || "Intermediate Level"}` :
           `🟢 ${t("miniESG.level.green") || "Good Level"}`}
        </div>
      </div>

      {/* Block Scores */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Environment */}
        <div className="border border-green-500/30 rounded-lg p-4 bg-[#0b0b0b]">
          <div className="text-sm text-green-400 mb-2">{t("miniESG.environment") || "Environment"}</div>
          <div className="text-2xl font-orbitron text-green-400">{score.environment} / 12</div>
          <div className="mt-2 h-2 bg-black rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 transition-all"
              style={{ width: `${(score.environment / 12) * 100}%` }}
            />
          </div>
        </div>

        {/* Social */}
        <div className="border border-blue-500/30 rounded-lg p-4 bg-[#0b0b0b]">
          <div className="text-sm text-blue-400 mb-2">{t("miniESG.social") || "Social"}</div>
          <div className="text-2xl font-orbitron text-blue-400">{score.social} / 10</div>
          <div className="mt-2 h-2 bg-black rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 transition-all"
              style={{ width: `${(score.social / 10) * 100}%` }}
            />
          </div>
        </div>

        {/* Governance */}
        <div className="border border-purple-500/30 rounded-lg p-4 bg-[#0b0b0b]">
          <div className="text-sm text-purple-400 mb-2">{t("miniESG.governance") || "Governance"}</div>
          <div className="text-2xl font-orbitron text-purple-400">{score.governance} / 8</div>
          <div className="mt-2 h-2 bg-black rounded-full overflow-hidden">
            <div
              className="h-full bg-purple-500 transition-all"
              style={{ width: `${(score.governance / 8) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Recommendations */}
      {store.recommendations.length > 0 && (
        <div className="border border-evat-neon/30 rounded-lg p-6 bg-[#0b0b0b]">
          <h3 className="text-lg font-orbitron text-evat-neon mb-4">{t("miniESG.recommendations") || "Recommendations"}</h3>
          <ul className="space-y-2">
            {store.recommendations.map((rec, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-neutral-300">
                <span className="text-evat-neon">•</span>
                <span>{rec}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}



